/**
 * Capacitor push notification registration (OneSignal).
 *
 * The Capacitor native shell bundles the OneSignal SDK (onesignal-cordova-plugin)
 * and initializes it natively. Inside the webview, Capacitor's Cordova bridge
 * exposes `window.OneSignal`. This module links push notifications to the signed-in
 * user by setting the OneSignal external id to the Rails user id — the exact same
 * targeting the backend already uses (`OneSignal::Sender` sends to external ids), so
 * no backend change is needed. This mirrors mobiloud/push_registration.js, which did
 * the same thing via MobiLoud's `nativeFunctions.onesignalSetExternalUserId`.
 *
 * OneSignal v5 API used here:
 *   - window.OneSignal.login(externalId)  — associate device with the user
 *   - window.OneSignal.logout()           — dissociate on sign-out
 *   - window.OneSignal.Notifications.requestPermission(true) — prompt if undecided
 */

import { isCapacitorEnvironment } from "./detection";

const BADGE_SYNC_ENDPOINT = "/api/v1/me/badge/sync";
const ONESIGNAL_WAIT_ATTEMPTS = 50; // 50 * 100ms = 5s, matching the MobiLoud path
const ONESIGNAL_WAIT_INTERVAL_MS = 100;

let initialized = false;

/**
 * Whether a user is authenticated. Rails sets window._tkcCurrentUserId in the
 * layout for authenticated requests (shared with the MobiLoud path).
 * @returns {boolean}
 */
export function isUserAuthenticated() {
  return typeof window !== "undefined" && !!window._tkcCurrentUserId;
}

/**
 * @returns {boolean} whether the OneSignal SDK bridge is available in the webview
 */
function isOneSignalReady() {
  return (
    typeof window !== "undefined" &&
    !!window.OneSignal &&
    typeof window.OneSignal.login === "function"
  );
}

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : null;
}

/**
 * Link this device to the current user in OneSignal via external id.
 * OneSignal v5 login() is async; await it so we only report success once the
 * link actually resolves, and swallow rejections (SDK not ready / offline) so
 * they never surface as unhandled promise rejections.
 * @returns {Promise<boolean>} whether the device was linked
 */
async function setOneSignalExternalUserId() {
  if (!isUserAuthenticated() || !isOneSignalReady()) {
    return false;
  }

  const userId = String(window._tkcCurrentUserId);

  try {
    await window.OneSignal.login(userId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Prompt for notification permission if the SDK exposes the API. Best-effort;
 * both synchronous throws and promise rejections are swallowed so push setup
 * never breaks the page.
 * @returns {Promise<void>}
 */
async function requestPermissionIfPossible() {
  try {
    if (window.OneSignal?.Notifications?.requestPermission) {
      // OneSignal v5: returns a promise; fall back to true (fallbackToSettings).
      await window.OneSignal.Notifications.requestPermission(true);
    }
  } catch {
    // no-op
  }
}

/**
 * Sync the app icon badge to the current unread count on launch. Same endpoint
 * the MobiLoud path used.
 */
async function syncBadge() {
  if (!isUserAuthenticated()) {
    return;
  }
  const csrfToken = getCsrfToken();
  if (!csrfToken) {
    return;
  }
  try {
    await fetch(BADGE_SYNC_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
    });
  } catch {
    // Badge sync is best-effort.
  }
}

/**
 * Resolve once the OneSignal bridge is available, or after the timeout.
 * @returns {Promise<boolean>} whether OneSignal became ready
 */
function whenOneSignalReady() {
  return new Promise((resolve) => {
    if (isOneSignalReady()) {
      resolve(true);
      return;
    }
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (isOneSignalReady()) {
        clearInterval(timer);
        resolve(true);
      } else if (attempts >= ONESIGNAL_WAIT_ATTEMPTS) {
        clearInterval(timer);
        resolve(false);
      }
    }, ONESIGNAL_WAIT_INTERVAL_MS);
  });
}

/**
 * Initialize Capacitor push handling. Safe to call multiple times and in any
 * environment — it no-ops outside the Capacitor shell.
 */
export function initCapacitorPush() {
  if (!isCapacitorEnvironment() || initialized) {
    return;
  }
  initialized = true;

  whenOneSignalReady().then(async (ready) => {
    if (!ready) {
      return;
    }
    await requestPermissionIfPossible();
    if (isUserAuthenticated()) {
      await setOneSignalExternalUserId();
    }
    await syncBadge();
  });
}

/**
 * Register the current device with the signed-in user (call after login).
 * @returns {Promise<boolean>} whether the external id was set
 */
export function registerCapacitorDevice() {
  if (!isCapacitorEnvironment()) {
    return Promise.resolve(false);
  }
  return whenOneSignalReady().then((ready) => {
    if (!ready) {
      return false;
    }
    return setOneSignalExternalUserId();
  });
}

/**
 * Dissociate this device from the user in OneSignal (call on logout).
 */
export function clearCapacitorDevice() {
  if (!isCapacitorEnvironment() || !isOneSignalReady()) {
    return;
  }
  try {
    // logout() is async in OneSignal v5; swallow both sync throws and promise
    // rejections so a sign-out never surfaces an unhandled rejection.
    const result = window.OneSignal.logout();
    if (result && typeof result.catch === "function") {
      result.catch(() => {});
    }
  } catch {
    // no-op
  }
}

export default {
  isUserAuthenticated,
  initCapacitorPush,
  registerCapacitorDevice,
  clearCapacitorDevice,
};
